import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG } from '../utils/constants';

// Types for API responses - UPDATED to match API documentation
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
  errors?: string[];
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

// Fleet API returns flat response structure
export interface FleetApiResponse<T> {
  data?: T; // Optional because sometimes API returns direct data
  [key: string]: any; // Allow any additional properties
}

// API documentation shows flat paginated response
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApiError {
  statusCode: number;
  message: string;
  error: string;
  timestamp: string;
  path: string;
}

interface AuthRefreshResponse {
  accessToken: string;
  expiresIn: number;
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private defaultTenantId: string;

  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.defaultTenantId = API_CONFIG.defaultTenantId;
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  private onTokenRefreshed(token: string) {
    this.refreshSubscribers.map((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token and tenantId
    this.api.interceptors.request.use(
      (config) => {
        // Skip adding Authorization header for auth endpoints that don't need it
        const skipAuth = config.url?.includes('/auth/login') || config.url?.includes('/auth/refresh');
        
        if (!skipAuth) {
          // Check both storages for the token
          const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add tenantId to fleet API requests
        if (config.url?.includes('/admin/fleet/')) {
          config.params = config.params || {};
          if (!config.params.tenantId) {
            config.params.tenantId = this.defaultTenantId;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle API documentation format vs current format
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // For fleet APIs, handle flat response structure
        if (response.config.url?.includes('/admin/fleet/')) {
          // API documentation shows direct data response
          // Wrap in our expected format for compatibility
          const data = response.data;
          
          // If it's already paginated response (has total, page, etc.), use as is
          if (data && typeof data === 'object' && 'total' in data && 'page' in data) {
            return {
              ...response,
              data: data
            };
          }
          
          // For single item responses, wrap the data
          return {
            ...response,
            data: data
          };
        }

        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors (token expired)
        if (error.response?.status === 401 && !originalRequest._retry) {
          // If already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.subscribeTokenRefresh((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.api(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Check both storages for refresh token
            const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
            
            if (refreshToken) {
              console.log('🔄 ApiService: Attempting token refresh...');
              
              // Backend expects 'refresh_token' in snake_case
              const refreshResponse = await this.api.post('/auth/refresh', {
                refresh_token: refreshToken,
              });

              // Backend returns direct data with 'access_token'
              const tokenData = refreshResponse.data;
              const newAccessToken = tokenData.access_token || (tokenData as any).accessToken;

              if (newAccessToken) {
                console.log('✅ ApiService: Token refreshed successfully');
                
                // Determine which storage to update
                if (localStorage.getItem('refreshToken')) {
                  localStorage.setItem('accessToken', newAccessToken);
                } else {
                  sessionStorage.setItem('accessToken', newAccessToken);
                }
                
                this.isRefreshing = false;
                this.onTokenRefreshed(newAccessToken);

                // Retry original request with new token
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return this.api(originalRequest);
              }
            }
          } catch (refreshError) {
            console.error('❌ ApiService: Token refresh failed:', refreshError);
            this.isRefreshing = false;
            
            // Refresh failed, clear everything from BOTH storages
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('refreshToken');
            sessionStorage.removeItem('user');
            
            // Redirect to login
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle API documentation error format
        const apiError: ApiError = {
          statusCode: error.response?.status || 500,
          message: error.response?.data?.message || error.message || 'An error occurred',
          error: error.response?.data?.error || 'Unknown Error',
          timestamp: error.response?.data?.timestamp || new Date().toISOString(),
          path: error.response?.data?.path || originalRequest.url || '',
        };

        return Promise.reject(apiError);
      }
    );
  }

  // Generic HTTP methods with tenantId support
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.get<T>(url, config);
      return response.data;
    } catch (error) {
      console.error('API GET Error:', error);
      throw error;
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      console.error('API POST Error:', error);
      throw error;
    }
  }


  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.put<T>(url, data, config);
      return response.data;
    } catch (error) {
      console.error('API PUT Error:', error);
      throw error;
    }
  }


  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.patch<T>(url, data, config);
      return response.data;
    } catch (error) {
      console.error('API PATCH Error:', error);
      throw error;
    }
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.delete<T>(url, config);
      return response.data;
    } catch (error) {
      console.error('API DELETE Error:', error);
      throw error;
    }
  }


  // Fleet-specific methods that handle tenantId automatically
  async fleetGet<T = any>(endpoint: string, params?: any): Promise<T> {
    const config: AxiosRequestConfig = {
      params: {
        tenantId: this.defaultTenantId,
        ...params
      }
    };
    return this.get<T>(`/admin/fleet${endpoint}`, config);
  }

  async fleetPost<T = any>(endpoint: string, data?: any): Promise<T> {
    const url = `/admin/fleet${endpoint}`;
    return this.post<T>(url, data);
  }

  async fleetPut<T = any>(endpoint: string, data?: any): Promise<T> {
    const url = `/admin/fleet${endpoint}`;
    return this.put<T>(url, data);
  }

  async fleetDelete<T = any>(endpoint: string): Promise<T> {
    const url = `/admin/fleet${endpoint}`;
    return this.delete<T>(url);
  }

  // Specialized methods for different content types
  async uploadFile<T = any>(url: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    try {
      const response = await this.api.post<ApiResponse<T>>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('API File Upload Error:', error);
      throw error;
    }
  }

  async downloadFile(url: string, filename?: string): Promise<void> {
    try {
      const response = await this.api.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('API File Download Error:', error);
      throw error;
    }
  }

  // Utility methods
  setAuthToken(token: string): void {
    this.api.defaults.headers.Authorization = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.api.defaults.headers.Authorization;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  setTenantId(tenantId: string): void {
    this.defaultTenantId = tenantId;
  }

  getTenantId(): string {
    return this.defaultTenantId;
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health');
      return true;
    } catch (error) {
      console.warn('API Health Check Failed:', error);
      return false;
    }
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService;